import { useCallback, useMemo } from 'react'
import { Power, PowerOff, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchRules, toggleRule, FRAUD_STR as S, type FraudRuleRow } from './fraudApi'

/**
 * A25.02 — Правила анти-фрода (rules engine). Enable/disable each rule. Dormant
 * until `/admin/antifraud/rules` ships. Placeholder permission USERS_VIEW.
 */
export function RulesScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<FraudRuleRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'condition', header: S.condition, type: 'text' },
      { key: 'action', header: S.action, type: 'text' },
      { key: 'enabled', header: S.enabled, type: 'text' },
      { key: 'hits', header: S.hits, type: 'number' },
    ],
    [],
  )

  const toggle = useCallback(
    async (row: FraudRuleRow) => {
      try {
        await toggleRule(row.id, !row.enabled)
        toast(S.toggled, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<FraudRuleRow>[]>(
    () => [
      {
        id: 'disable',
        label: S.disable,
        icon: <PowerOff size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: toggle,
      },
      {
        id: 'enable',
        label: S.enable,
        icon: <Power size={14} />,
        visible: (r) => !r.enabled,
        onSelect: toggle,
      },
    ],
    [toggle],
  )

  const fetcher = useCallback(fetchRules, [])
  return (
    <>
      <PageHeader title={S.rules} screenId="A25.02" />
      <DataTable<FraudRuleRow>
        screenId="A25.02"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <SlidersHorizontal size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
